import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import * as Sentry from '@sentry/nextjs';
import { css } from '@styled-system/css';
import React, { useCallback, useEffect, useState } from 'react';

import { Alert, Box, Button, Collapse, Icon, Loader } from '~/components/blocks';
import { AppointmentQualificationConfirmationMethod } from '~/graphql/generated/graphql';
import { isPdf } from '~/utils/file';
import { openWithNoOpener } from '~/utils/window';

import { ImgViewer } from './ImgViewer';
import { PdfViewer } from './PdfViewer';
import { useFetchInsuranceCards } from './use-fetch-insurance_cards';

const DEFAULT_HEIGHT = '130px';

type Props = {
  patientId?: string;
  isActivePatient?: boolean;
  onClickCertificate?: () => void;
  qualificationConfirmationMethod: AppointmentQualificationConfirmationMethod;
};

const PreviewButton = styled(Button)(({ theme }) =>
  css({
    position: 'absolute',
    top: '50%',
    left: '50%',
    display: 'flex',
    width: '40px',
    height: '40px',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.radii.circle,
    opacity: 0,
    transform: 'translate(-50%,-50%)',
    transition: `opacity linier ${theme.transitions.fast}`,
  }),
);

const Content = styled(Box)(({ theme }) =>
  css({
    backgroundColor: theme.colors.background.bg,
    cursor: 'pointer',
    minHeight: DEFAULT_HEIGHT,
    position: 'relative',
    [`&:hover > ${PreviewButton}`]: {
      opacity: 1,
    },
  }),
);

export const InsuranceCardCollapse = React.memo((props: Props) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(true);
  const [hasPdfError, setHasPdfError] = useState(false);

  const willFetchInsuranceCards =
    props.qualificationConfirmationMethod === 'insurance_card' && isOpen;

  const { loading, timeout, hasCertificate, error, insuranceCards } = useFetchInsuranceCards(
    willFetchInsuranceCards,
    !!props.onClickCertificate,
    props.patientId,
  );
  const hasPdf = insuranceCards.some((card) => isPdf(card.attachment.contentType));

  const handleTitleClick = useCallback(() => setIsOpen((_isOpen) => !_isOpen), []);
  const handleClick = useCallback(() => {
    if (props.patientId) {
      openWithNoOpener(`/insurance_card_preview/${props.patientId}`, {
        target: props.patientId,
        width: 640,
        height: 720,
        bgColor: '#525659',
      });
    }
  }, [props.patientId]);

  const handlePdfError = useCallback((error: Error) => {
    setHasPdfError(true);
    Sentry.captureMessage(`react-pdf Error: message=${error.message}`);
  }, []);
  const handlePdfLoad = useCallback(() => setIsPdfLoading(false), []);

  useEffect(() => {
    // 表示患者が変更された場合
    setIsOpen(false);
  }, [props.patientId]);

  return (
    <Collapse
      open={isOpen}
      label={
        <>
          <Icon size="xxl" icon="insurance" />
          保険情報
        </>
      }
      onClick={handleTitleClick}
    >
      {props.qualificationConfirmationMethod === 'individual_number_card' ? (
        <Alert status="info" marginTop={theme.space.m}>
          オンライン資格確認を希望しています。服薬指導日までに、オンライン資格確認等システムなどにて資格情報をご確認ください。確認できない場合は、患者様へ同意登録を促してください
        </Alert>
      ) : timeout ? (
        <Alert marginTop={theme.space.m}>
          資格確認書の取得に時間がかかっています。
          <br />
          しばらく待ってから、再度アクセスしてください
        </Alert>
      ) : error ? (
        <Box padding={theme.space.m}>
          <Alert status="error">取得できませんでした</Alert>
        </Box>
      ) : !props.isActivePatient ? (
        <Box padding={theme.space.m}>
          <Alert status="info">退会済みのため表示できません</Alert>
        </Box>
      ) : loading ? (
        <Box
          backgroundColor={theme.colors.background.bg}
          height={DEFAULT_HEIGHT}
          position="relative"
        >
          <Loader open inside logoSize="half" />
        </Box>
      ) : insuranceCards.length > 0 ? (
        <Content onClick={hasPdf && (hasPdfError || isPdfLoading) ? undefined : handleClick}>
          {insuranceCards.map((card) =>
            isPdf(card.attachment.contentType) ? (
              <PdfViewer
                key={card.attachment.id}
                src={card.attachment.url}
                onError={handlePdfError}
                onLoad={handlePdfLoad}
              />
            ) : (
              <ImgViewer
                key={card.attachment.id}
                alt={card.attachment.title}
                src={card.attachment.url}
              />
            ),
          )}
          <PreviewButton icon use="base">
            <Icon color="white" icon="blank" size="l" />
          </PreviewButton>
        </Content>
      ) : (
        <Box padding={theme.space.m}>
          <Alert status="info">登録されていません</Alert>
        </Box>
      )}
      {hasCertificate && props.onClickCertificate && (
        <Box
          textAlign="right"
          marginBottom={theme.space.xxl}
          backgroundColor={theme.colors.background.bg}
        >
          <Button use="white" size="m" onClick={props.onClickCertificate}>
            <Icon icon="document" size="xl" color="black" />
            公費受給者証を確認
          </Button>
        </Box>
      )}
    </Collapse>
  );
});

InsuranceCardCollapse.displayName = 'InsurnceCardCollapse';
